// TODO: Properly type this file: https://github.com/webflow/webflow/issues/26407
/*::
import type {JSONNode} from '@packages/shared/types/JSONNode';
*/

/**
 * Contains JsonDomUtils shared by server and client
 * NOTE: This runs on the front-end and back-end
 */
import {reportBug} from '@packages/utilities/reporter';
import type {RichTextValue} from '@packages/systems/dynamo/binding-props';
import {
  rootIds,
  indexJsonDomNodeArrayById,
  hasFlattenedOverrides,
  filterDivergentRichTextNodes,
  parseDefaultData,
} from '@packages/systems/core/render';
import {isNodePojo} from '@packages/systems/core/nodepojo';
import {getElementDataAndBindForJSONNode} from '@packages/systems/core/component-conversions';
import {constantFalse, emptyArray} from '@packages/utilities/fp/utils';
import {entries} from '@packages/utilities/key-map';
import {
  emitComponentInstanceWhtmlMarkup,
  createWhtmlCaptureController,
  type AnnotationCtx as WhtmlAnnotationCtx,
  type WhtmlParserDeps,
} from '@packages/systems/cms/whtml';
import {getSymbolRootIdFromInstance} from './JsonNodeComponentUtils';
import {
  forEachChild,
  forEachComponentPropertyChild,
} from './JsonNodeTraversalUtils';

import * as GuidUtils from '@packages/utilities/guid';
import htmlparser from 'htmlparser2';

import {
  defaults,
  includes,
  isString,
  cloneDeep,
  trim,
  forOwn,
  pick,
  omitBy,
  isUndefined,
} from 'lodash';

import SharedConfig, {
  RTE_EMBED_TYPE_ATTR,
} from '@packages/systems/core/constants/SharedConfig';
import {CUSTOM_CODE_CHAR_LIMIT} from '@packages/domains/designer-workspace/page-management/data-types';
import * as DomTypeUtils from '@packages/systems/core/utils/DomTypeUtils';
import * as UrlUtils from '@packages/systems/core/utils/UrlUtils';
import {
  isFieldOfItemRef,
  getValueFieldSlug,
  getItemRefSlug,
} from '@packages/systems/dynamo/utils/ParamFieldPathUtils';
import * as TextUtils from '@packages/systems/core/utils/TextUtils';
import {
  TAG_BY_NODE_TYPE,
  GET_FROM_DATA,
  TagName,
} from '@packages/systems/core/constants/TagToTypeMigration';
import {CONDITION_INVISIBLE_CLASS} from '@packages/systems/dynamo/constants';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {
  type Type,
  Types,
  Plugins,
  Component,
  Expressions,
  Variable,
  getSystem,
  matchExpression,
} from '@packages/systems/wfdl';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  getName,
  getPluginName,
  QualifiedMap,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import {type Json, parse} from '@packages/utilities/json';
import {weakMemo} from '@packages/utilities/memo';
import {parseCollectionListPropTypeName} from '@packages/systems/dynamo/utils/CollectionListPropNameUtils';

const {
  WEBFLOW_NODE_ID_ATTR,
  RTE_FIGURE_TYPE_ATTR,
  RTE_FIGURE_ALIGN_ATTR,
  RTE_FIGURE_MAX_WIDTH_ATTR,
  RTE_FIGURE_MAX_HEIGHT_ATTR,
  RTE_FIGURE_TYPE_CLASS,
  FIGURE_ALIGN_MAP,
  DYN_BOUND_PLACEHOLDER_TEXT,
  VOID_HTML_TAGS,
} = SharedConfig;

const TAG_TYPE_MAP = {
  p: 'Paragraph',
  strong: 'Strong',
  em: 'Emphasized',
  sup: 'Superscript',
  sub: 'Subscript',
  a: 'Link',
  span: 'Span',
  h1: 'Heading',
  h2: 'Heading',
  h3: 'Heading',
  h4: 'Heading',
  h5: 'Heading',
  h6: 'Heading',
  blockquote: 'Blockquote',
  ol: 'List',
  ul: 'List',
  li: 'ListItem',
  img: 'Image',
  figure: 'Figure',
  figcaption: 'Figcaption',
  iframe: 'Iframe',
  br: 'LineBreak',
  div: 'Block',
  code: 'InlineCode',
} as const;

// Attribute added to links to other pages of the same site.
// Used in the public API endpoints that read and update dom nodes.
export const pageLinkAttr = 'data-wf-link-page-id';

// @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
const getJsonNodeTag = (node) => {
  if (!node.type) {
    return node.tag;
  }

  if (TAG_BY_NODE_TYPE[node.type] === GET_FROM_DATA) {
    // NOTE: If a rich content node has `node.type` but not `node.data.tag` then
    // something went wrong with the node migration. Fall back to `node.tag`
    // for now. Later on we might need to add report bug here.
    const tag = node.data && node.data.tag;

    if (!tag) {
      console.error(
        'getJsonNodeTag: RTE node has `node.type` but not `node.data.tag`'
      );
      return node.tag;
    }

    return tag;
  }

  return TAG_BY_NODE_TYPE[node.type] || 'div';
};

export const getNodeMaps = (
  jsonNodes: Array<JSONNode>
): {
  nodeMap: Map<JSONNode['_id'], JSONNode>;
  parentMap: Map<JSONNode['_id'], JSONNode['_id']>;
} => {
  const nodeMap = new Map();
  const parentMap = new Map();

  for (let i = 0; i < jsonNodes.length; i++) {
    const node = jsonNodes[i];
    // @ts-expect-error - TS18048 - 'node' is possibly 'undefined'.
    nodeMap.set(node._id, node);

    // @ts-expect-error - TS2345 - Argument of type 'JSONNode | undefined' is not assignable to parameter of type 'JSONNode'.
    forEachChild(node, (childId) => {
      // @ts-expect-error - TS18048 - 'node' is possibly 'undefined'.
      parentMap.set(childId, node._id);
    });
  }
  return {nodeMap, parentMap};
};

/**
 * Creatas a new jsonNode
 * @param  {Object}   opts initialization options
 * @return {jsonNode}
 */
export const createJsonNode = (opts: any) => {
  opts = defaults({}, opts, {
    guid: undefined,
    tag: undefined,
    text: undefined,
    value: undefined,
    attributes: {},
    data: {},
    classes: [],
  });

  const node: any = {
    _id: opts.guid,
  };

  if (opts.text) {
    node.text = true;
    node.v = opts.value;
  } else {
    node.tag = opts.tag;
    node.children = [];
    node.classes = opts.classes;

    const hasData = Boolean(Object.keys(opts.data).length);
    const hasAttributes = Boolean(Object.keys(opts.attributes).length);

    if (hasData || hasAttributes) {
      node.data = opts.data || {};
    }

    if (hasAttributes) {
      node.data.attr = opts.attributes;
    }
  }

  return node;
};

const getValidNodes = (
  parentNode: JSONNode,
  target?: 'page-content' | 'rich-text',
  supportHtmlEmbeds?: boolean
) => {
  let VALID_NODES = SharedConfig.VALID_RICH_TEXT_NODES;

  if (target === 'page-content') {
    VALID_NODES = SharedConfig.VALID_PAGE_CONTENT_NODES;
  } else if (supportHtmlEmbeds && parentNode?.type === 'HtmlEmbed') {
    VALID_NODES = SharedConfig.VALID_HTML_EMBED_NODES;
  }

  return VALID_NODES;
};

// Align the RTE HTML embed limit with the shared custom-code character limit
const CHARACTER_LIMIT = CUSTOM_CODE_CHAR_LIMIT;

// Bounds the `parseRichTextBody` recursion (nested richtext prop parsing) so a
// deeply-nested `<wf-component>`/`<wf-prop type="richtext">` chain from the
// Data API can't stack-overflow the process.
const MAX_RICHTEXT_PROP_DEPTH = 10;

type RTEHtmlOptions = {
  removeRootLevelSpaces: boolean;
  allowDynLink: boolean;
  jsonNodes: Array<JSONNode>;
  addType: boolean;
  onError: any;
  supportHtmlEmbeds?: boolean;
  target?: 'page-content' | 'rich-text';
  styleIdMap?: Record<string, string>;
  conversionType?: ConversionTypeEnum;
  seedId?: string;
  // opt-in to expand WHTML `<wf-component>` markup back into the
  // Symbol JSONNode subtree on write; off = treat it as regular RTF content.
  parseComponentInstanceWfdl?: boolean;
  // Site ComponentMap, required when `parseComponentInstanceWfdl` is true.
  components?: Component.ComponentMap;
  // Injected WHTML parser runtime (HTMLToWFDLParser + getBootstrappedPlugins),
  // required when `parseComponentInstanceWfdl` is true. Injected by the server
  // caller so `core` never depends on the html-to-webflow / corePlugins chains.
  whtmlParserDeps?: WhtmlParserDeps;
  // called when a `<wf-component>` fails to parse/resolve, or a prop override
  // can't be applied. The parse stays tolerant (never throws); the Data API
  // write path uses this to reject with a 400 naming the offending component.
  onComponentInstanceParseError?: (info: {
    instanceId: string | null;
    markup: string;
    message: string;
    reason?:
      | 'forbidden-element'
      | 'unknown-prop'
      | 'prop-type-mismatch'
      | 'nested-component-in-prop';
    // Prop name for prop-level failures, so consumers can name the prop.
    propName?: string;
    // Which forbidden element the governance check hit (`forbidden-element`
    // only), so consumers can show slot- vs collection-list-specific copy.
    forbiddenElementType?: 'DynamoWrapper' | 'Slot';
  }) => void;
  // Recursion depth for nested `<wf-prop type="richtext">` parsing; internal,
  // set by `parseRichTextBody` itself. Callers should not set this.
  richTextPropDepth?: number;
  // Shares the outer parse's discovered-id list across `parseRichTextBody`
  // recursion so a `data-w-id` collision inside a richtext prop body is
  // still caught against ids used elsewhere in the same document.
  sharedDiscoveredIds?: Array<string>;
};
/**
 * Converts a string of HTML to an array of JSON nodes.
 * NOTE: This should only be used in the context of legacy RTE node conversion.
 * @param  {String}             html                       A string of HTML, usually from a `v` value of a legacy RTE node.
 * @param  {Boolean}            opts.removeRootLevelSpaces Set to true for rich text nodes, false for typographic nodes
 * @param  {Boolean}            opts.allowDynLink          If false, will convert dynamic page links to external links (should be false until
 *                                                         designer link settings supports dynamic page links outside dynlists)
 * @param  {Boolean}            opts.jsonNodes             Existing domNodes to merge with if found in html string
 * @param  {Boolean}            opts.addType               Add `type` property to created json nodes.
 * @param  {Function}           opts.onError               Optional htmlparser error handler
 * @param  {Boolean}            opts.supportHtmlEmbeds     Whether to support HTML embeds in the conversion
 * @param                       opts.target                Conversion only for rich text content or for broader page content.
 * @param                       opts.styleIdMap            A map where the keys are the classnames and the values are the corresponding style block ids, to be able to map correctly classnames to ids (and preserve inline styling)
 * @param  {ConversionTypeEnum} opts.conversionType        Enum to customize conversion output
 * @return {Object}
 *            {Array}   jsonContentNodes           An array of node jsonNodes of the parsed content
 *            {Array}   rootChildIds               An array of root child ids
 */
export const rteHtmlToJsonNodes = (
  html: string | undefined,
  _opts: Partial<RTEHtmlOptions>
): {
  jsonContentNodes: JSONNode[];
  rootChildIds: string[];
  nextId: string;
} => {
  const opts = defaults({}, _opts, {
    removeRootLevelSpaces: true,
    allowDynLink: false,
    jsonNodes: [],
    addType: false,
    onError: undefined,
    supportHtmlEmbeds: false,
    target: 'rich-text',
    richTextPropDepth: 0,
  }) as RTEHtmlOptions;

  let nextId = opts.seedId || '';

  if (!html || !isString(html)) {
    return {
      jsonContentNodes: [],
      rootChildIds: [],
      nextId,
    };
  }

  const {nodeMap: existingNodesMap} = getNodeMaps(opts.jsonNodes);

  const parseOptions = {
    decodeEntities: true, // This is required for security, see https://github.com/fb55/htmlparser2/issues/105
    recognizeSelfClosing: true,
  } as const;

  const rootNode = createJsonNode({guid: 'root'});
  const jsonNodes: Array<JSONNode> = [];
  const stack = [rootNode]; // Start with a virtual root node on the stack so we always have a parent node
  const indexPath: Array<any> = []; // A path to the current node by index, e.g. [2, 3, 0] => 3rd child's 4th child's 1st child
  // @ts-expect-error - TS7034 - Variable 'previousSiblingTextNode' implicitly has type 'any' in some locations where its type cannot be determined.
  let previousSiblingTextNode;
  let isInsideEmbedDepth = -1; // index used to keep track of the current depth while inside of an HTML embed (-1 means we're outside of an embed)
  // Legacy bugs may have introduced duplicate wf ids in the html string.
  // Shared with the outer call (via `sharedDiscoveredIds`) when this is a
  // recursive `parseRichTextBody` invocation, so a collision inside a nested
  // richtext prop body is still caught against ids used elsewhere.
  const discoveredIds: Array<any> = opts.sharedDiscoveredIds ?? [];

  // Resolve the id to assign to a captured `<wf-component>` root, mirroring the
  // same guard the normal element path applies to `data-w-id` (see below): keep
  // the stashed id only when it's a fresh GUID that isn't transient/micro and
  // hasn't been seen yet; otherwise mint a fresh id. Prevents a captured
  // component from being persisted with the same `_id` as another parsed node.
  // The caller (`finalize`) records the produced node ids in `discoveredIds`,
  // so this does not push here.
  const reserveCapturedInstanceId = (candidate: string): string => {
    const isMicroNode = candidate.length === SharedConfig.microNodeIdLength;
    const isTransient = candidate.indexOf('transient') > -1;
    const isDuplicate = includes(discoveredIds, candidate);
    if (
      !(isTransient || isMicroNode || isDuplicate) &&
      GuidUtils.isGuid(candidate)
    ) {
      return candidate;
    }
    if (nextId) {
      const id = nextId;
      nextId = GuidUtils.incrementGuid(nextId);
      return id;
    }
    return GuidUtils.generateGuid();
  };

  // `<wf-component>` WHTML capture lives in
  // `@packages/systems/cms/whtml`; the htmlparser2 callbacks below delegate to it.
  const whtmlCapture = createWhtmlCaptureController({
    parseComponentInstanceWfdl: opts.parseComponentInstanceWfdl,
    components: opts.components,
    parserDeps: opts.whtmlParserDeps,
    // Parse-side mirror of `renderNodes` on emit. `jsonNodes` stays default —
    // a captured prop body is a self-contained fragment. `seedId`/`nextId`
    // and `discoveredIds` ARE shared (see below) to avoid duplicate ids
    // across the recursion boundary; `richTextPropDepth` bounds the recursion.
    parseRichTextBody: (propHtml) => {
      const depth = (opts.richTextPropDepth ?? 0) + 1;
      if (depth > MAX_RICHTEXT_PROP_DEPTH) {
        opts.onComponentInstanceParseError?.({
          instanceId: null,
          markup: propHtml,
          message: `richtext prop nesting exceeds max depth of ${MAX_RICHTEXT_PROP_DEPTH}`,
        });
        return {jsonContentNodes: [], rootChildIds: []};
      }
      const result = rteHtmlToJsonNodes(propHtml, {
        target: 'rich-text',
        removeRootLevelSpaces: opts.removeRootLevelSpaces,
        allowDynLink: opts.allowDynLink,
        addType: opts.addType,
        supportHtmlEmbeds: opts.supportHtmlEmbeds,
        parseComponentInstanceWfdl: opts.parseComponentInstanceWfdl,
        components: opts.components,
        whtmlParserDeps: opts.whtmlParserDeps,
        onComponentInstanceParseError: opts.onComponentInstanceParseError,
        richTextPropDepth: depth,
        seedId: nextId,
        sharedDiscoveredIds: discoveredIds,
      });
      nextId = result.nextId;
      return result;
    },
    onError: opts.onComponentInstanceParseError,
    getCaptureParent: () => stack[stack.length - 1] ?? rootNode,
    jsonNodes,
    discoveredIds,
    resolveInstanceId: reserveCapturedInstanceId,
  });

  const parser = new htmlparser.Parser(
    {
      onopentag: (tag, attributes) => {
        // Inside an HtmlEmbed the body is raw user HTML that the embed branch
        // below preserves verbatim into `data.embed.meta.html`; don't let WHTML
        // capture consume a literal `<wf-component>` out of it.
        const insideHtmlEmbed =
          opts.supportHtmlEmbeds &&
          stack[stack.length - 1]?.type === 'HtmlEmbed';
        // delegate WHTML `<wf-component>` capture (true = consumed).
        if (!insideHtmlEmbed && whtmlCapture.handleOpenTag(tag, attributes)) {
          // A captured component is inserted as its own sibling in
          // parent.children; clear the preceding text node so text that
          // follows the component starts a fresh node instead of coalescing
          // into the text that came before it (e.g. `before<wf-component/>after`
          // must not render as `beforeafter` ahead of the component).
          previousSiblingTextNode = null;
          return;
        }

        const parentNode = stack[stack.length - 1];

        const VALID_NODES = getValidNodes(
          parentNode,
          opts.target,
          opts.supportHtmlEmbeds
        );

        // skip over invalid tags. Any valid descendants will just become children of their nearest valid ancestor
        if (!VALID_NODES.has(tag)) {
          return;
        }

        // webflow-specific attributes can be deleted, especially legacy ones
        let ignoreAttributes = [
          SharedConfig.WEBFLOW_NODE_ID_ATTR,
          'data-dyn-path',
          SharedConfig.RTE_LEGACY_TEMP_ELEM_ATTR,
        ];
        if (opts.supportHtmlEmbeds && parentNode?.type === 'HtmlEmbed') {
          let attributesToString = '';
          for (const [attr, value] of Object.entries(attributes)) {
            const operators = [
              '+',
              '-',
              '*',
              '/',
              '==',
              '===',
              '&',
              '&&',
              '||',
              '|',
            ];
            if (
              !ignoreAttributes.includes(attr) &&
              // These shouldn't be allowed within the attributes list
              operators.every(
                (operator) => operator !== attr && operator !== value
              )
            ) {
              attributesToString += `${attr}${value ? `='${value}'` : ''} `;
            }
          }
          attributesToString = attributesToString.trim();
          // We're just going to assume everything inside an HTML embed is valid,
          // as it's up to the end users to provide valid HTML.

          parentNode.data.embed.meta.html += `<${tag}${
            attributesToString.length &&
            attributesToString.length <= CHARACTER_LIMIT
              ? ` ${attributesToString}`
              : ''
          }>`;

          if (!VOID_HTML_TAGS.has(tag)) {
            // Increment the index to know how deep we are within the embed
            // note: we don't increment this value for void tags since void
            // tags shouldn't have children
            isInsideEmbedDepth++;
          }
          return;
        }
        indexPath.push(parentNode.children.length);

        // Re-use the node id saved in the html unless it's a legacy transient node
        const wfNodeIdVal = attributes[SharedConfig.WEBFLOW_NODE_ID_ATTR] || '';
        const existingNode = existingNodesMap.get(wfNodeIdVal);
        const existingData = existingNode && existingNode.data;
        let existingClasses = existingNode && existingNode.classes;

        // If there are any passed-in classes and a styleIdMap is provided,
        // then map the class names to the corresponding style block ids
        // (this preserves any inline styling there might be)
        if (opts.styleIdMap && attributes.class) {
          existingClasses = (existingClasses ?? []).concat(
            attributes.class
              .split(' ')
              .reduce<string[]>((existingClassnames, className) => {
                const styleId = opts.styleIdMap?.[className];
                if (styleId) {
                  existingClassnames.push(styleId);
                }

                return existingClassnames;
              }, [])
          );
        }

        // detect legacy micro/transient node ids (note: transient node ids were only persisted for a brief time)
        const isMicroNode =
          wfNodeIdVal.length === SharedConfig.microNodeIdLength;
        const isTransient = wfNodeIdVal.indexOf('transient') > -1;
        const isDuplicate = includes(discoveredIds, wfNodeIdVal);

        const guid: string = (() => {
          if (
            wfNodeIdVal &&
            !(isTransient || isMicroNode || isDuplicate) &&
            GuidUtils.isGuid(wfNodeIdVal)
          )
            return wfNodeIdVal;

          if (nextId) {
            const id = nextId;
            nextId = GuidUtils.incrementGuid(nextId);
            return id;
          }

          return GuidUtils.generateGuid();
        })();

        discoveredIds.push(guid);

        const inlineStyles = attributes.style;
        const styleArray = inlineStyles ? inlineStyles.split(/; ?/) : [];
        const styleObj: Record<string, any> = {};
        styleArray.forEach((styleRule) => {
          if (styleRule.length) {
            const ruleSplit = styleRule.split(/: ?/);
            // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
            styleObj[ruleSplit[0]] = ruleSplit[1];
          }
        });

        if (isMicroNode && existingData) {
          // eslint-disable-next-line webflow/no-delete
          delete existingData.micro;
        }

        // Class attributes added by the RTE don't need to be persisted as an attribute and can be dropped from the attributes list.
        ignoreAttributes = [
          ...ignoreAttributes,
          'class',
          'style',
          'contenteditable',
        ];

        ignoreAttributes.forEach((key) => {
          if (key in attributes) {
            // eslint-disable-next-line webflow/no-delete
            delete attributes[key];
          }
        });

        const isLinkTag = tag === 'a';
        const isListTag = Boolean(tag === 'ul' || tag === 'ol');
        const isListItemTag = tag === 'li';

        const newNode = createJsonNode({
          guid,
          tag,
          attributes,
          data: existingData,
          classes: existingClasses,
        });

        const customClass = attributes['data-custom-class'];

        if (customClass) {
          // eslint-disable-next-line webflow/no-delete
          delete attributes['data-custom-class'];
          newNode.data = newNode.data || {};
          newNode.data.customclass = customClass;
        }

        if (opts.addType) {
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly p: "Paragraph"; readonly strong: "Strong"; readonly em: "Emphasized"; readonly sup: "Superscript"; readonly sub: "Subscript"; readonly a: "Link"; readonly span: "Span"; readonly h1: "Heading"; ... 14 more ...; readonly div: "Block"; }'.
          newNode.type = TAG_TYPE_MAP[tag] || 'Block';
          newNode.data = newNode.data || {};
          newNode.data.tag = tag;
        }

        if (isLinkTag) {
          if (opts.conversionType === ConversionTypeEnum.FOR_LOCALIZATION) {
            if (attributes[pageLinkAttr]) {
              // If we are converting for the public API and the link has the page link
              // attribute then add the correct data for the page to the attributes.
              attributes[SharedConfig.RTE_LINK_TYPE_ATTR] = 'page';
              attributes[SharedConfig.RTE_LINK_PAGE_ID_ATTR] =
                attributes[pageLinkAttr];
              // eslint-disable-next-line webflow/no-delete
              delete attributes[pageLinkAttr];
              // Override the href value to be the default since we are not using it
              // in this case and we could be adding a useless value.
              attributes.href = '#';
            } else if (!existingData) {
              // Preserve current behavior when there is no existing data.
              attributes[SharedConfig.RTE_LINK_TYPE_ATTR] ||= 'external';
            } else if (
              existingData.link.mode === 'external' &&
              attributes.href
            ) {
              // Allow updating existing external links.
              attributes[SharedConfig.RTE_LINK_TYPE_ATTR] = 'external';
            }
            // Preserve existing target value.
            if (!attributes.target && existingData?.link?.target) {
              attributes.target = existingData.link.target;
            }
          }

          newNode.data = setLinkDataFromAttributes(
            newNode.data ?? {},
            attributes,
            opts.allowDynLink
          );
        } else if (tag === 'figure') {
          newNode.data = setFigureDataFromAttributes(newNode.data, attributes);

          // Set figure Classes
          const figureType = newNode.data.figure.type;
          const figureAlign = newNode.data.figure.align;
          const figureTypeclass =
            figureType === 'image'
              ? SharedConfig.RTE_FIGURE_TYPE_IMAGE_CLASS
              : SharedConfig.RTE_FIGURE_TYPE_VIDEO_CLASS;
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ center: "w-richtext-align-center"; fullwidth: "w-richtext-align-fullwidth"; normal: "w-richtext-align-normal"; floatleft: "w-richtext-align-floatleft"; floatright: "w-richtext-align-floatright"; }'.
          const figureAlignClass = SharedConfig.FIGURE_ALIGN_MAP[figureAlign];

          newNode['@class'] = [figureTypeclass, figureAlignClass];

          if (figureType === 'video') {
            const paddingBottom = newNode.data.figure.maxHeight;
            styleObj['padding-bottom'] = paddingBottom;
          }
        } else if (isListTag || isListItemTag) {
          const listType = isListTag ? 'list' : 'item';
          const listData = {
            list: {
              type: listType,
            },
          } as const;

          newNode.data = {...newNode.data, ...listData};
        } else if (tag === 'iframe') {
          newNode.data.iframe = {
            type: 'video',
          };
        } else if (
          tag === 'div' &&
          attributes[RTE_EMBED_TYPE_ATTR] === 'true'
        ) {
          // eslint-disable-next-line webflow/no-delete
          delete attributes[RTE_EMBED_TYPE_ATTR];

          newNode.type = 'HtmlEmbed';
          newNode.data.embed = {
            meta: {
              html: '',
              div: false,
              iframe: false,
              script: false,
            },
            type: 'html',
          };
          newNode.data.insideRTE = true;
          newNode.v = '';

          // We're now inside of an HTML embed, so we need to keep track
          // of the depth so we know when we can escape out of it
          isInsideEmbedDepth = 0;
        }

        parentNode.children.push(guid);

        jsonNodes.push(newNode);
        stack.push(newNode);
        previousSiblingTextNode = null;
      },
      ontext: (text) => {
        // accumulate text into the active WHTML capture.
        if (whtmlCapture.handleText(text)) {
          return;
        }
        const parentNode = stack[stack.length - 1];
        if (DomTypeUtils.isDynBoundHtml(parentNode.data)) {
          // If the parent node has dynbound html, then this text can be thrown out.
          // Should only see this when a content node was dynbound while editing with RTE 1.x
          // and given placeholder text.
        } else if (
          opts.removeRootLevelSpaces &&
          parentNode === rootNode &&
          trim(text) === ''
        ) {
          // If we have spaces between root-level tags, ignore them
          // @ts-expect-error - TS7005 - Variable 'previousSiblingTextNode' implicitly has an 'any' type.
        } else if (previousSiblingTextNode) {
          // htmlparser2 will decode entities into their own text nodes (with their own ontext events)
          // If there's a text node adjacent to another text node, join them
          previousSiblingTextNode.v += text;
        } else if (opts.supportHtmlEmbeds && parentNode?.type === 'HtmlEmbed') {
          // If the parent node is an HTML embed, then we want to add the text directly
          // to the parent node's embedded metadata
          parentNode.data.embed.meta.html += text;
        } else {
          indexPath.push(parentNode.children.length);

          const guid: string = (() => {
            if (nextId) {
              const id = nextId;
              nextId = GuidUtils.incrementGuid(nextId);
              return id;
            }
            return GuidUtils.generateGuid();
          })();

          const newNode = createJsonNode({guid, text: true, value: text});
          parentNode.children.push(guid);

          previousSiblingTextNode = newNode;
          jsonNodes.push(newNode);
          indexPath.pop();
        }
      },
      oncomment: (comment) => {
        // re-serialize comments into the active WHTML capture.
        if (whtmlCapture.handleComment(comment)) {
          return;
        }
        // Preserve HTML comments inside an HtmlEmbed but remove outside of it
        if (opts.supportHtmlEmbeds && isInsideEmbedDepth >= 0) {
          const parentNode = stack[stack.length - 1];
          if (parentNode?.type === 'HtmlEmbed') {
            parentNode.data.embed.meta.html += `<!--${comment}-->`;
          }
        }
      },
      // @ts-expect-error - TS2322 - Type '(tag: any) => void' is not assignable to type '() => void'. | TS7006 - Parameter 'tag' implicitly has an 'any' type.
      onclosetag: (tag) => {
        // re-serialize closes; controller finalizes on the outer close.
        if (whtmlCapture.handleCloseTag(tag)) {
          return;
        }
        const parentNode = stack[stack.length - 1];

        const VALID_NODES = getValidNodes(
          parentNode,
          opts.target,
          opts.supportHtmlEmbeds
        );

        // skip over invalid tags. Any valid descendants will just become children of their nearest valid ancestor
        if (!VALID_NODES.has(tag)) {
          return;
        }
        if (opts.supportHtmlEmbeds && parentNode?.type === 'HtmlEmbed') {
          if (VOID_HTML_TAGS.has(tag)) {
            // since void tags don't have a closing tag in HTML5,
            // we don't want to add a closing tag nor change the embed depth
            // (as void tags shouldn't have children). We'll early return here
            // so we don't end up running the rest of the logic below.
            return;
          }
          if (isInsideEmbedDepth > 0) {
            // We're just going to assume everything inside an HTML embed is valid,
            // as it's up to the end users to provide valid HTML.
            parentNode.data.embed.meta.html += `</${tag}>`;

            // if we've already reached the limit then we need to clear the html embed attributes
            if (parentNode.data.embed.meta.html.length > CHARACTER_LIMIT) {
              parentNode.data.embed.meta.html = '';
            }

            parentNode.v = parentNode.data.embed.meta.html;
            isInsideEmbedDepth--;
            return;
          } else if (isInsideEmbedDepth === 0) {
            // We're now outside of the HTML embed, so we need to reset the depth
            isInsideEmbedDepth = -1;
          }
        }
        indexPath.pop();
        stack.pop();
        previousSiblingTextNode = null;
      },
      onerror: (error) => {
        opts.onError && opts.onError(error);
      },
    },
    parseOptions
  );

  parser.parseChunk(html);
  parser.done();

  return {
    jsonContentNodes: jsonNodes,
    rootChildIds: rootNode.children,
    nextId,
  };
};

export enum ConversionTypeEnum {
  FOR_DATA_MANAGER,
  FOR_PUBLISH,
  FOR_DATA_API,
  FOR_LOCALIZATION,
}

export const isComponentInstance = (node: JSONNode) =>
  Boolean(node?.data?.sym?.inst);

/**
 * Converts an array of jsonNodes to an HTML string.
 * NOTE: This should only be used in the context of converting jsonNodes to legacy RTE 1.x compatable HTML for editing.
 * @param  {Object[]}             jsonNodes                  An array of node JSON objects as stored on the server.
 * @param  {Object}               classNameMap               A map of classIds to classNames
 * @param  {ConversionTypeEnum}   conversionType             Enum to customize conversion output
 * @param  {Boolean}              supportHtmlEmbeds          Whether to support HTML embeds in the conversion
 * @param  {Boolean}              emitComponentInstanceWfdl  Emit Component Instances as `<wf-component>` WHTML (Data API round-trip); default false = legacy output.
 * @return {String} An HTML representation of those nodes.
 */
export const rteJsonNodesToHtml = (
  plugins: Plugins.PluginsType,
  dataTypes: Plugins.DataTypes,
  jsonNodes: any,
  classNameMap: Record<any, any> | undefined,
  conversionType: ConversionTypeEnum | undefined,
  supportHtmlEmbeds?: boolean,
  emitComponentInstanceWfdl?: boolean,
  /**
   * Annotation ctx (Public Data API only): adds decorative `name=`/`label=`
   * attributes (parser ignores them); internal callers pass undefined.
   */
  whtmlAnnotate?: WhtmlAnnotationCtx,
  // Injected error reporter for `<wf-component>` emit failures (runs server-side);
  // the caller wires logging + a counter. Omitted by client/internal callers.
  onComponentInstanceEmitError?: (info: {
    instanceId: string;
    message: string;
    error?: unknown;
  }) => void
) => {
  const htmlFragments: Array<
    | any
    | undefined
    | string
    | TagName
    | {
        getTagFromData: true;
      }
  > = [];
  const nodeIndex = indexJsonDomNodeArrayById(jsonNodes);
  const rootNodeIds = rootIds(plugins, dataTypes)(jsonNodes);

  // @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
  const walkJsonTree = (node) => {
    if (!node) {
      return;
    }

    // `node.type === 'Symbol'` guards against line-break nodes, which also
    // carry `data.sym.inst` (e.g. `'LineBreak'`) but must serialize as `<br>`,
    // not `<wf-component>`. (`isComponentInstance` only checks `data.sym.inst`.)
    if (
      emitComponentInstanceWfdl &&
      node?.type === 'Symbol' &&
      isComponentInstance(node)
    ) {
      htmlFragments.push(
        emitComponentInstanceWhtmlMarkup({
          instanceNode: node,
          nodeIndex,
          annotate: whtmlAnnotate,
          onError: onComponentInstanceEmitError,
          renderNodes: (subtree) =>
            rteJsonNodesToHtml(
              plugins,
              dataTypes,
              subtree,
              classNameMap,
              conversionType,
              supportHtmlEmbeds,
              true,
              whtmlAnnotate,
              // Thread the error reporter through so a nested instance's emit
              // failure is reported too (the markup falls back either way).
              onComponentInstanceEmitError
            ),
        })
      );
      return;
    }

    if (
      // If we are not supporting html embeds and there is a node type or tag
      (!supportHtmlEmbeds && (node.type || node.tag)) ||
      // Or if we are supporting html embeds and the node is not an html embed
      (supportHtmlEmbeds &&
        node.type !== 'HtmlEmbed' &&
        (node.type || node.tag))
    ) {
      const tag = getJsonNodeTag(node);

      htmlFragments.push('<', tag);
      const attr: {
        [key: string]: unknown;
      } = (node.data && node.data.attr) || {};

      const linkAttributes =
        tag === 'a' && node.data ? getRteLinkAttributesFromData(node.data) : {};
      const figureAttributes =
        tag === 'figure' && node.data
          ? getRteFigureAttributesFromData(node.data)
          : {};
      const styleBlockIds = node.classes || [];
      const classes: Array<any | null | string> = [];
      const inlineStyles: Array<any | string> = [];
      // NOTE: dyn-binding content nodes is currently disabled here for now
      // it's also disabled in the designer (ExternalStore.js)
      const innerHtmlDynSlug = false; //_.get(node, 'data.dyn.bind.innerHTML[0].slug');
      const contentEditableAttr: {
        [key: string]: unknown;
      } = innerHtmlDynSlug
        ? {'data-dyn-path': innerHtmlDynSlug, contenteditable: false}
        : {};

      const customClass = node && node.data && node.data.customclass;
      let customClassAttr: Record<string, any> = {};

      if (customClass) {
        classes.push(customClass);
        customClassAttr = {'data-custom-class': customClass};
      }

      if (tag === 'figure') {
        const figureAlign = figureAttributes[RTE_FIGURE_ALIGN_ATTR];
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ center: "w-richtext-align-center"; fullwidth: "w-richtext-align-fullwidth"; normal: "w-richtext-align-normal"; floatleft: "w-richtext-align-floatleft"; floatright: "w-richtext-align-floatright"; }'.
        const figureAlignClass = FIGURE_ALIGN_MAP[figureAlign];
        const figureType = figureAttributes[RTE_FIGURE_TYPE_ATTR];
        const figureTypeClass = RTE_FIGURE_TYPE_CLASS + figureType;

        classes.push(figureTypeClass, figureAlignClass);

        // Set figure inline styles
        const figureMaxWidth = figureAttributes[RTE_FIGURE_MAX_WIDTH_ATTR];
        const figureMaxHeight = figureAttributes[RTE_FIGURE_MAX_HEIGHT_ATTR];

        if (figureMaxWidth) {
          inlineStyles.push('max-width:' + figureMaxWidth);
        }

        if (figureType === 'video') {
          inlineStyles.push('padding-bottom:' + figureMaxHeight);
          if (figureMaxWidth) {
            inlineStyles.push('width:' + figureMaxWidth);
          }
        }
      }

      // On the client we derive class names from styleBlockStore via class ids.
      // On the server during publish we have @class set with the proper class names
      if (classNameMap) {
        // @ts-expect-error - TS7006 - Parameter 'blockGuid' implicitly has an 'any' type.
        styleBlockIds.forEach((blockGuid) => {
          const className = classNameMap[blockGuid];
          if (className) {
            classes.push(TextUtils.cssifyName(className));
          }
        });
      }

      if (classes.length) {
        attr.class = classes.join(' ');
      }

      const inlineStyleAttr: {
        [key: string]: unknown;
      } = inlineStyles.length ? {style: inlineStyles.join(';')} : {};

      const idAttr: {
        [key: string]: unknown;
      } =
        conversionType === ConversionTypeEnum.FOR_DATA_MANAGER
          ? {}
          : {[WEBFLOW_NODE_ID_ATTR]: node._id};

      let rteAttrs: {[key: string]: unknown};

      switch (conversionType) {
        case ConversionTypeEnum.FOR_PUBLISH: {
          rteAttrs = {
            ...attr,
            ...inlineStyleAttr,
            ...figureAttributes,
          };
          break;
        }
        case ConversionTypeEnum.FOR_DATA_API: {
          rteAttrs = {
            ...attr,
            ...(linkAttributes?.href ? {href: linkAttributes.href} : null),
            ...inlineStyleAttr,
            ...figureAttributes,
          };
          break;
        }
        case ConversionTypeEnum.FOR_LOCALIZATION: {
          let linkAttr = {};
          if (
            linkAttributes[SharedConfig.RTE_LINK_TYPE_ATTR] === 'page' &&
            linkAttributes[SharedConfig.RTE_LINK_PAGE_ID_ATTR]
          ) {
            const pageId = linkAttributes[SharedConfig.RTE_LINK_PAGE_ID_ATTR];
            linkAttr = {[pageLinkAttr]: pageId};
            // Delete the default href attribute since it's not used in this case.
            // eslint-disable-next-line webflow/no-delete
            delete attr.href;
          } else if (linkAttributes?.href) {
            linkAttr = {href: linkAttributes.href};
          }

          const baseAttrs = {
            ...attr,
            ...linkAttr,
            ...inlineStyleAttr,
            ...figureAttributes,
          };

          rteAttrs =
            // Root nodes should not have the node id attribute.
            rootNodeIds.includes(node._id)
              ? baseAttrs
              : {...baseAttrs, ...idAttr};
          break;
        }
        // In packages/systems/designer/validation/ValidationErrorStoreUtils/updateErrors.ts:785
        // we use this function in a function pipeline without specifying a conversion type
        // Prior to 5703a95, the function would effectively default to FOR_DATA_MANAGER
        default:
        case ConversionTypeEnum.FOR_DATA_MANAGER: {
          rteAttrs = {
            ...idAttr,
            ...attr,
            ...linkAttributes,
            ...figureAttributes,
            ...inlineStyleAttr,
            ...contentEditableAttr,
            ...customClassAttr,
          };
          break;
        }
      }

      forOwn(rteAttrs, (value, key) => {
        const stringVal = TextUtils.encodeDoubleQuotes(String(value));
        htmlFragments.push(` ${key}="${stringVal}"`);
      });

      htmlFragments.push('>');

      if (!VOID_HTML_TAGS.has(tag)) {
        if (innerHtmlDynSlug) {
          htmlFragments.push(DYN_BOUND_PLACEHOLDER_TEXT);
        } else if (node.children && node.children.length) {
          // @ts-expect-error - TS7006 - Parameter 'childId' implicitly has an 'any' type.
          node.children.forEach((childId) => walkJsonTree(nodeIndex[childId]));
        }

        htmlFragments.push('</' + (tag as any) + '>');
      }
    } else if (node.v) {
      if (supportHtmlEmbeds && node.type === 'HtmlEmbed') {
        // we need to wrap the embed in a div with a special attribute to allow
        // CSV import behavior to correctly identify its children as an embed
        htmlFragments.push(`<div ${RTE_EMBED_TYPE_ATTR}='true'>`);
        htmlFragments.push(node.v);
        htmlFragments.push(`</div>`);
      } else if (node.text) {
        const textValue = TextUtils.encodeTextValue(node.v);
        htmlFragments.push(textValue);
      }
    }
  };

  rootNodeIds.forEach((rootId) => walkJsonTree(nodeIndex[rootId]));

  return htmlFragments.join('');
};

/**
 * Returns RTE Link Attributes from a jsonNode data object for Designer Preview (temp fix)
 * @param  {Object} data         a jsonNode data object
 * @return {Object}              a map of rte link attributes
 */
export const buildRteLinkAttrsForPreview = (nodeData: any) => {
  return pick(getRteLinkAttributesFromData(nodeData), [
    SharedConfig.RTE_LINK_TYPE_ATTR,
    SharedConfig.RTE_LINK_ITEM_ID_ATTR,
    SharedConfig.RTE_LINK_COLLECTION_ID_ATTR,
    SharedConfig.RTE_LINK_PAGE_ID_ATTR,
    SharedConfig.RTE_LINK_DOM_ID_ATTR,
  ]);
};

/**
 * Returns RTE Link Attributes from a jsonNode data object
 * @param  {Object} data         a jsonNode data object
 * @return {Object}              a map of rte link attributes
 */
export const getRteLinkAttributesFromData = (data: any) => {
  data = data || {};
  const attr = data.attr || {};
  const link = data.link || {};
  const block = data.block;
  const rtLinkMode = block === 'inline' ? 'ext-link' : link.mode;
  const href = link.url || attr.href || '#';

  const rteAttrs: Record<string, any> = {};

  rteAttrs[SharedConfig.RTE_LINK_TYPE_ATTR] = rtLinkMode;
  rteAttrs[SharedConfig.RTE_LINK_ITEM_ID_ATTR] = link.itemId;
  rteAttrs[SharedConfig.RTE_LINK_COLLECTION_ID_ATTR] = link.collectionId;
  rteAttrs[SharedConfig.RTE_LINK_PAGE_ID_ATTR] = link.pageId;
  rteAttrs[SharedConfig.RTE_LINK_DOM_ID_ATTR] = link.domId;
  rteAttrs.target = link.target;
  rteAttrs.href = href;

  return omitBy(rteAttrs, isUndefined);
};

/**
 * Takes attributes parsed from an rte link element and returns a deep cloned jsonNode data object with the converted attribute data
 * @param {Object} data                   jsonNode data as a javascript object
 * @param {Object} attributes             link element attributes parsed from html
 * @param {Boolean} options.allowDynLink   Whether dynamic links should be converted to external links
 * @return {Object} a modified deep clone of the data object.
 */
export const setLinkDataFromAttributes = (
  data: {
    [key: string]: any;
  },
  attributes: {
    [key: string]: any;
  },
  allowDynLink: boolean = true
) => {
  const _data = cloneDeep(data);
  _data.attr = restoreHrefInAttrs(_data.attr);
  attributes = restoreHrefInAttrs(attributes);

  // Exctract link attributes
  const href = attributes.href;
  const target = attributes.target;
  const rteLinkType = attributes[SharedConfig.RTE_LINK_TYPE_ATTR];
  const pageId = attributes[SharedConfig.RTE_LINK_PAGE_ID_ATTR];
  const itemId = attributes[SharedConfig.RTE_LINK_ITEM_ID_ATTR];
  const collectionId = attributes[SharedConfig.RTE_LINK_COLLECTION_ID_ATTR];
  const domId = attributes[SharedConfig.RTE_LINK_DOM_ID_ATTR];

  // Ensure RTE link data attributes haven't made their way into the data object.
  if (_data.attr) {
    SharedConfig.RTE_LINK_DATA_ATTRS.forEach((name) => {
      // eslint-disable-next-line webflow/no-delete
      delete _data.attr[name];
    });
  }

  // The RTE will add RTE_LINK_TYPE_ATTR attribute to indicate that it edited some aspect of the anchor like the type of anchor
  // the designer settings UI will remove the link mode.
  // If it did edit the link from the editor-canvas we need to update the domNode models here to reflect those changes
  if (rteLinkType) {
    const url = href || '';
    let tel = '';
    let email = '';
    let subject = '';

    if (rteLinkType === 'email') {
      // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
      email = UrlUtils.parseEmailAddress(url);
      // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
      subject = UrlUtils.parseEmailSubject(url);
    } else if (rteLinkType === 'tel') {
      // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
      tel = UrlUtils.parseTel(url);
    }

    const mode = (() => {
      if (rteLinkType === 'page' && itemId) {
        // Dynamic Detail page
        // Note: links to dynamic detail pages are not currently supported in the designer link settings UI
        // Updating links to dyn-detail pages are also never updated on the server when the slug changes (requires linkMap)
        // So we just convert it to an external link type for now.
        // In preview mode however we actually want to follow the link, and need to know it's a dyn link
        if (allowDynLink) {
          return 'dynpage';
        } else {
          return 'external';
        }
      } else if (rteLinkType === 'ext-link') {
        _data.block = 'inline';
        return 'external';
      } else {
        return rteLinkType;
      }
    })();

    // all link types require these data properties
    if (!_data.attr) {
      _data.attr = {};
    }
    _data.attr.href = url;

    // set/remove the target attribute
    if (target) {
      _data.attr.target = '_blank';
    } else if (_data.attr.target) {
      // eslint-disable-next-line webflow/no-delete
      delete _data.attr.target;
    }

    // set the new link object
    const linkData = (() => {
      if (mode === 'external') {
        return target ? {mode, url, target} : {mode, url};
      }
      if (mode === 'page') {
        return target ? {mode, pageId, target} : {mode, pageId};
      }
      if (mode === 'dynpage') {
        return target
          ? {mode: 'page', itemId, collectionId, target}
          : {mode: 'page', itemId, collectionId};
      }
      if (mode === 'section') {
        return {mode, domId};
      }
      if (mode === 'email') {
        return subject ? {mode, email, subject} : {mode, email};
      }
      if (mode === 'tel') {
        return {mode, tel};
      }
    })();

    _data.link = omitBy(linkData, isUndefined);
  }

  return _data;
};

/**
 * Returns a deep cloned jsonNode data object updated by converting RT figure data-attributes into jsonNode data
 * @param {Object} data       a jsonNode data object
 * @param {Object} attributes figure element attributes parsed from html
 */
export const setFigureDataFromAttributes = (data: any, attributes: any) => {
  const _data = cloneDeep(data) || {};
  attributes = attributes || {};

  const type = attributes[SharedConfig.RTE_FIGURE_TYPE_ATTR];
  const align = attributes[SharedConfig.RTE_FIGURE_ALIGN_ATTR];
  const maxWidth = attributes[SharedConfig.RTE_FIGURE_MAX_WIDTH_ATTR];
  const maxHeight = attributes[SharedConfig.RTE_FIGURE_MAX_HEIGHT_ATTR];
  const dimensions = attributes[SharedConfig.RTE_FIGURE_DIMENSIONS_ATTR];
  const pageUrl = attributes[SharedConfig.RTE_FIGURE_PAGE_URL_ATTR];

  if (_data.attr) {
    SharedConfig.RTE_FIGURE_DATA_ATTRS.forEach((name) => {
      // eslint-disable-next-line webflow/no-delete
      delete _data.attr[name];
    });
  }

  _data.figure = omitBy(
    {
      type,
      align,
      maxWidth,
      maxHeight,
      dimensions,
      pageUrl,
    },
    isUndefined
  );

  _data.style = _data.style || {};

  if (maxWidth) {
    _data.style['max-width'] = maxWidth;
  } else if (_data.style['max-width']) {
    // eslint-disable-next-line webflow/no-delete
    delete _data.style['max-width'];
  }

  const isVideoFigure = type === 'video';
  if (isVideoFigure) {
    _data.style['padding-bottom'] = maxHeight;

    if (maxWidth) {
      _data.style.width = maxWidth;
    } else if (_data.style.width) {
      // eslint-disable-next-line webflow/no-delete
      delete _data.style.width;
    }
  }

  if (_data.style && !Object.keys(_data.style).length) {
    // eslint-disable-next-line webflow/no-delete
    delete _data.style;
  }

  return _data;
};

/**
 * Returns RTE Figure Attributes from a jsonNode data object
 * @param  {Object} data  a jsonNode data object for a figure element
 * @return {Object}       A map of rte figure attributes
 */
export const getRteFigureAttributesFromData = (data: any) => {
  data = data || {};
  const figure = data.figure || {};
  const rteAttrs: Record<string, any> = {};

  rteAttrs[SharedConfig.RTE_FIGURE_TYPE_ATTR] = figure.type;
  rteAttrs[SharedConfig.RTE_FIGURE_ALIGN_ATTR] = figure.align;
  rteAttrs[SharedConfig.RTE_FIGURE_MAX_WIDTH_ATTR] = figure.maxWidth;
  rteAttrs[SharedConfig.RTE_FIGURE_MAX_HEIGHT_ATTR] = figure.maxHeight;
  rteAttrs[SharedConfig.RTE_FIGURE_DIMENSIONS_ATTR] = figure.dimensions;
  rteAttrs[SharedConfig.RTE_FIGURE_PAGE_URL_ATTR] = figure.pageUrl;

  return omitBy(rteAttrs, isUndefined);
};

/**
 * Toggles off safe href and removes href-disabled attributes from a map of attributes
 * @param  {Object} attributes
 * @return {Object}
 */
export const restoreHrefInAttrs = (attributes: any) => {
  const attrs = cloneDeep(attributes) || {};

  attrs[SharedConfig.HREF] =
    attrs[SharedConfig.HREF] || attrs[SharedConfig.HREF_DISABLED];

  // eslint-disable-next-line webflow/no-delete
  delete attrs[SharedConfig.HREF_DISABLED];
  // eslint-disable-next-line webflow/no-delete
  delete attrs[SharedConfig.HREF_DISABLED_DEFAULT_COLOR];
  // eslint-disable-next-line webflow/no-delete
  delete attrs[SharedConfig.HREF_DISABLED_UNDERLINE];

  return attrs;
};

export {replaceNodeSubtree} from './replaceNodeSubtree';

/**
 * Iterates over an array of jsonNodes replacing old descendants of a specific node with new ones, Typically used when
 * swapping out TextHolder descendants
 * @param  {Array}  jsonNodes            jsonNode array to iterate over. ie: all the nodes on a page
 * @param  {String} subtreeRootId        the Guid of the root node, eg: the TextHolder node
 * @param  {Array}  subtreeRootChildIds  An array of ids for the new subtree nodes that are direct children of the root node.
 * @param  {Array}  newSubtreeNodes      a jsonNode array with just the new content nodes
 * @param  {Object} newData              If also updating the data of the root subtree node, ie: the TextHolder node
 * @return {Array}                       the full jsonNode array (what was jsonNodes) with subtree replaced.
 */
export const __DEPRECATED__replaceNodeSubtree = (
  jsonNodes: any,
  subtreeRootId: any,
  subtreeRootChildIds: any,
  newSubtreeNodes: any,
  newData: any
) => {
  jsonNodes = jsonNodes || [];
  newSubtreeNodes = newSubtreeNodes || [];
  subtreeRootChildIds = subtreeRootChildIds || [];

  // @ts-expect-error - TS7006 - Parameter 'context' implicitly has an 'any' type.
  return processNodes(jsonNodes, function processNodesCallback(context) {
    const {
      node: {jsonNode},
      // eslint-disable-next-line webflow/deprecated
      fn: {__DEPRECATED__dropChildren},
    } = context;

    const updatedSubtreeNodes = [jsonNode];

    if (jsonNode._id === subtreeRootId) {
      if (jsonNode.v) {
        // eslint-disable-next-line webflow/no-delete
        delete jsonNode.v;
      }

      // Drop the old children

      __DEPRECATED__dropChildren(jsonNode);

      if (subtreeRootChildIds.length) {
        const newSubtreeNodesLength = newSubtreeNodes.length;

        // Concat the new content nodes
        for (let i = 0; i < newSubtreeNodesLength; i++) {
          const node = newSubtreeNodes[i];
          updatedSubtreeNodes.push(node);
        }

        jsonNode.children = subtreeRootChildIds;
      }

      if (newData) {
        jsonNode.data = newData;
      }
    }

    return updatedSubtreeNodes;
  });
};

/**
 * Processes an array of jsonNodes, node by node. Used for patching and expanding nodes.
 *
 * The onNode() callback will receive the context object with context.node referencing the node being processed, and can return
 * - undefined                 will assume no changes to the jsonTree (You can still modify the jsonNode directly)
 * - [jsonNode]                same as returning undefined
 * - []                        will remove the jsonNode from the tree and from the nodeMap (its children and descendants will remain in the tree)
 * - [jsonNode, ...otherNodes] will concat otherNodes to the jsonTree after jsonNode
 * - [...otherNodes]           will concat otherNodes to the jsonTree instead of jsonNode
 *
 * Use the onNode return api in conjunction with context.fn methods to process nodes and the jsonTree
 *
 * @param  {Array}    jsonNodes      jsonNodes to process
 * @param  {Function} onNode         called on each node with the jsonNode, and a function to drop its descendants
 * @return {Array}                   processed jsonNodes
 */
export const processNodes = (jsonNodes: Array<JSONNode>, onNode: any) => {
  let droppedDescendantIds: Array<string> = [];

  // @ts-expect-error - TS7034 - Variable 'processedNodes' implicitly has type 'any[]' in some locations where its type cannot be determined.
  const processedNodes = [];
  const newNodePositions = new Map();
  const {nodeMap, parentMap} = getNodeMaps(jsonNodes);

  // Drops the children of a node and their descendants from the jsonTree.
  // (does not remove the jsonNode itself)

  const __DEPRECATED__dropChildren = (jsonNode: JSONNode) => {
    const childIds = jsonNode.children || [];

    if (childIds.length) {
      // Note: we'll encounter and drop each of this new generation of childIds before
      // continuing in the list, so concat them to the beginning of the
      // droppedDescendantIds array.
      droppedDescendantIds = childIds.concat(droppedDescendantIds);
      childIds.forEach((childId) => {
        context.tree.nodeMap.delete(childId);
        context.tree.parentMap.delete(childId);
      });

      jsonNode.children = [];
    }
  };

  // Used only in `dropSubtrees`, this function drops the children of a node and
  // their descendants from the jsonTree while also dropping overrides fields (w/
  // flattened overrides) we find along the way so we don't have an orphaned nodes.
  // (does not remove the jsonNode itself)
  const _dropChildren = (jsonNode: JSONNode) => {
    const childIds = jsonNode.children || [];

    if (childIds.length) {
      // Note: we'll encounter and drop each of this new generation of childIds before continuing in the list,
      // so concat them to the beginning of the droppedDescendantIds array.
      droppedDescendantIds = childIds.concat(droppedDescendantIds);

      for (const childId of childIds) {
        const childNode = context.tree.nodeMap.get(childId);

        if (childNode) {
          dropSubtrees(childNode);

          context.tree.nodeMap.delete(childId);
          context.tree.parentMap.delete(childId);
        }
      }

      jsonNode.children = [];
    }
  };

  // Used only in `dropSubtrees`, this function drops a user component instance's
  // overrides from the jsonTree.
  // (does not remove the jsonNode itself)
  const _dropOverrides = (jsonNode: JSONNode) => {
    const overrides = jsonNode?.data?.sym?.overrides;

    if (overrides) {
      for (const [field, overrideValue] of entries(overrides)) {
        // Make sure we're working with flattened overrides
        if (hasFlattenedOverrides(overrideValue)) {
          droppedDescendantIds = overrideValue.concat(droppedDescendantIds);

          for (const overrideId of overrideValue) {
            const overrideNode = context.tree.nodeMap.get(overrideId);
            if (overrideNode) {
              dropSubtrees(overrideNode);
            }

            context.tree.nodeMap.delete(overrideId);
            context.tree.parentMap.delete(overrideId);
          }

          // eslint-disable-next-line webflow/no-delete
          delete jsonNode?.data?.sym?.overrides[field];
        }
      }
    }
  };

  // Drops `children` and flattened override fields from `data.sym.overrides` (if they exist).
  const dropSubtrees = (jsonNode: JSONNode) => {
    _dropChildren(jsonNode);
    _dropOverrides(jsonNode);
  };

  // Can be called from onNode() to drop a node with a higher index than the one currently being processed
  // ie: if we're processing jsonNodes[4] this can be used to drop any node jsonNodes[5+]
  // Note: If you want to drop the current node and its children use (__DEPRECATED__dropChildren() and return []) instead of this
  // eslint-disable-next-line webflow/deprecated
  const __DEPRECATED__dropNodeAndChildren = (nodeToDrop: JSONNode) => {
    const descendantId = nodeToDrop._id;

    // Drop the nodeToDrop from the jsonTree
    // Note: by adding it to droppedDescendantIds array, we're effectively dropping its entire subtree
    // so we don't need to call __DEPRECATED__dropChildren(). See __DEPRECATED__dropChildren() for why we unshift here.
    droppedDescendantIds.unshift(descendantId);

    // remove the nodeToDrop from the tree map
    context.tree.nodeMap.delete(descendantId);
    context.tree.parentMap.delete(descendantId);

    // Find the nodeToDrop's parent.
    const parentNode = jsonNodes.find((parentCandidate) => {
      return (
        parentCandidate.children &&
        parentCandidate.children.includes(descendantId)
      );
    });

    // We know that if parentNode exists, it must have a `children` array.
    // This is because the `jsonNodes.find` callback required the candidate node
    // to have a children array property with the descendantId included in the array.
    // The children check is to satisy flow.
    if (parentNode && parentNode.children) {
      // Remove the reference to this node from its parent.
      parentNode.children = parentNode.children.filter((childId) => {
        return childId !== descendantId;
      });
    }
  };

  // Used only in `dropNodeAndSubtrees` to drop the children of a future
  // node while also dropping overrides if they're in the children subtree.
  const _dropNodeAndChildren = (nodeToDrop: JSONNode) => {
    const nodeToDropId = nodeToDrop._id;

    // Find the nodeToDrop's parent.
    const parentNode = jsonNodes.find(
      (parentCandidate) =>
        parentCandidate.children &&
        parentCandidate.children.includes(nodeToDropId)
    );

    // We know that if parentNode exists, it must have a `children` array.
    // This is because the `jsonNodes.find` callback required the candidate node
    // to have a children array property with the nodeToDropId included in the array.
    // The children check is to satisfy flow.
    if (parentNode && parentNode.children) {
      const updatedChildren: Array<string> = [];

      for (const childId of parentNode.children) {
        const childNode = context.tree.nodeMap.get(childId);

        // Remove any subtrees from the child node if they exist.
        if (childNode && childId === nodeToDropId) {
          dropSubtrees(childNode);
        }

        // Remove reference to `nodeToDropId` from parent children array.
        if (childId !== nodeToDropId) {
          updatedChildren.push(childId);
        }
      }

      parentNode.children = updatedChildren;
    }

    // Drop the nodeToDrop from the jsonTree
    droppedDescendantIds.unshift(nodeToDropId);

    // remove the nodeToDrop from the tree map
    context.tree.nodeMap.delete(nodeToDropId);
    context.tree.parentMap.delete(nodeToDropId);
  };

  // Used only in `dropNodeAndSubtrees` to drop an overrideId from an overrides
  // field of a future node.
  const _dropOverrideFromField = (overrideToDrop: JSONNode) => {
    const overrideIdToDrop = overrideToDrop._id;

    // Find the nodeToDrop's parent.
    for (const parentCandidate of jsonNodes) {
      const overrides = parentCandidate?.data?.sym?.overrides;

      if (overrides) {
        // @ts-expect-error - TS18048 - 'parentCandidate.data' is possibly 'undefined'.
        parentCandidate.data.sym.overrides = entries(overrides).reduce<
          Record<string, any>
        >((fields, [field, overrideValue]: [any, any]) => {
          // Make sure we're working with flattened overrides
          if (hasFlattenedOverrides(overrideValue)) {
            // Filter out overrideIdToDrop from the array.
            const updatedOverrides = overrideValue.filter(
              (overrideId) => overrideId !== overrideIdToDrop
            );

            if (updatedOverrides.length) {
              fields[field] = updatedOverrides;
            }
          } else {
            // Don't touch non-flattened overrides
            fields[field] = overrideValue;
          }

          return fields;
        }, {});
      }
    }
  };

  // This function can be called from onNode() to drop a node with a higher index
  // than the one currently being processed ie: if we're processing jsonNodes[4]
  // this can be used to drop any node jsonNodes[5+].
  const dropNodeAndSubtrees = (nodeToDrop: JSONNode) => {
    if (!nodeToDrop) return;
    _dropNodeAndChildren(nodeToDrop);
    _dropOverrideFromField(nodeToDrop);
  };

  // Run every node in the subtree through a cb function
  // eslint-disable-next-line webflow/deprecated
  const __DEPRECATED__scanSubtree = (
    node: JSONNode,
    cb: (arg1: JSONNode) => void
  ) => {
    const children = node.children || [];

    children.forEach((childId) => {
      const child = context.tree.nodeMap.get(childId);
      if (child) {
        cb(child);

        __DEPRECATED__scanSubtree(child, cb);
      }
    });
  };

  // Formally `__DEPRECATED__scanSubtree`, this function runs every node in the `children` and
  // `data.sym.overrides` subtrees through a cb function.
  const _scanSubtreesIterator = (
    nodeId: string,
    cb: (arg1: JSONNode) => void
  ) => {
    const node = context.tree.nodeMap.get(nodeId);
    if (node) {
      cb(node);
      scanSubtrees(node, cb);
    }
  };

  // Run every node in a JSONNodes subtrees through a cb function
  const scanSubtrees = (node: JSONNode, cb: (arg1: JSONNode) => void) => {
    if (!node) return;
    forEachChild(node, (childId) => _scanSubtreesIterator(childId, cb));
  };

  // Inserts nodes after the node with anchorNodeId is processed
  const insertNodesAfter = (
    anchorNodeId: JSONNode['_id'],
    newNodes: Array<JSONNode>
  ) => {
    newNodePositions.set(anchorNodeId, newNodes);
  };

  // Init the context object (internal processing state)
  const context = {
    tree: {
      jsonNodes, // The src jsonTree
      nodeMap, // Maintained internally
      parentMap,
    },
    node: {
      // This is replaced for every onNode() call.
      jsonNode: undefined,
      newSubtree: undefined,
    },
    fn: {
      __DEPRECATED__dropChildren,

      __DEPRECATED__dropNodeAndChildren,

      __DEPRECATED__scanSubtree,
      dropNodeAndSubtrees,
      dropSubtrees,
      insertNodesAfter,
      scanSubtrees,
    },
  } as const;

  jsonNodes.forEach((jsonNode) => {
    const shouldDropDescendantFromTree =
      droppedDescendantIds.length &&
      includes(droppedDescendantIds, jsonNode._id);

    // Because overrides are flattened and persisted to the root tree, we have to
    // account for their children when dropping overrides or else they'll be orphaned.
    if (shouldDropDescendantFromTree) {
      __DEPRECATED__dropChildren(jsonNode);
    } else {
      // @ts-expect-error - TS2540 - Cannot assign to 'node' because it is a read-only property.
      context.node = {
        jsonNode,
        newSubtree: [jsonNode],
      };

      // onNode can return a falsy value if no changes to the tree are being made at the current index
      // return an empty array to delete the node, or an array with more nodes to add them add this index.
      const newSubtree = onNode(context) || [jsonNode];
      const jsonNodeWasDropped = !newSubtree.some(
        // @ts-expect-error - TS7006 - Parameter '_jsonNode' implicitly has an 'any' type.
        (_jsonNode) => _jsonNode._id === jsonNode._id
      );

      // New nodes added without a parent will be assumed to be added
      // at the current parent level. We need to get this value here
      // before we potentially remove the current node from the parent
      // map below.
      const anchorParentId = parentMap.get(jsonNode._id);

      // If deleting the current node remove it from the nodeMaps
      if (jsonNodeWasDropped) {
        context.tree.nodeMap.delete(jsonNode._id);
        context.tree.parentMap.delete(jsonNode._id);
      }

      const newNodesAtThisPosition = newNodePositions.get(jsonNode._id) || [];

      // @ts-expect-error - TS7006 - Parameter 'subtree' implicitly has an 'any' type.
      const adjustNodeMaps = (subtree) => {
        for (let i = 0; i < subtree.length; i++) {
          const node = subtree[i];
          nodeMap.set(node._id, node);
          if (node.children) {
            forEachChild(node, (childId) => {
              parentMap.set(childId, node._id);
            });
          }
          processedNodes.push(node);
        }
        // In the first for loop we set all the newly found children of new subtree
        // nodes correctly to the parentMap, we need to also find the top level
        // nodes from that subtree and set their parent as the anchorParent
        // to correct insert the subtree back to the full json array
        for (let i = 0; i < subtree.length; i++) {
          const node = subtree[i];
          if (!parentMap.has(node._id) && anchorParentId) {
            parentMap.set(node._id, anchorParentId);
          }
        }
      };

      // Make sure any new nodes added are in the nodeMap and parentMap
      adjustNodeMaps(newSubtree);
      adjustNodeMaps(newNodesAtThisPosition);
    }
  });

  // @ts-expect-error - TS7005 - Variable 'processedNodes' implicitly has an 'any[]' type.
  return processedNodes;
};

export const isUsedSymbol = (jsonSymbols: any, jsonNodes: any) => {
  // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments.
  const symbolsMap = jsonSymbols.reduce<Record<string, any>>(
    // @ts-expect-error - TS7006 - Parameter 'result' implicitly has an 'any' type. | TS7006 - Parameter 'symbol' implicitly has an 'any' type.
    (result, symbol) => {
      result[symbol._id] = symbol;
      return result;
    },
    {}
  );

  // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments. | TS7006 - Parameter 'ids' implicitly has an 'any' type. | TS7006 - Parameter 'node' implicitly has an 'any' type.
  const usedSymbolIds = jsonNodes.reduce<Record<string, any>>((ids, node) => {
    const symbolRootId = getSymbolRootIdFromInstance(node);
    return symbolRootId
      ? Object.assign(ids, getSymbolsIdsMap(symbolsMap, symbolRootId))
      : ids;
  }, {});

  return (symbol: any) => Boolean(symbol && usedSymbolIds[symbol._id]);
};

export const hasDateFilters = (
  jsonNodes: JSONNode[],
  jsonSymbols: JSONNode[],
  dynCollection: string | null | undefined,
  collections: {
    [collectionId: string]: {
      fields: {
        slug: string;
        type: string;
        validations?: {collectionId?: string};
      }[];
    };
  }
): boolean => {
  const rootNodeId = jsonNodes[0]?._id;

  if (!rootNodeId) {
    return false;
  }

  const jsonNodeMap = new Map<string, JSONNode>();
  // Inline getNodeMap calls to avoid needing to call spread.
  for (let i = 0; i < jsonNodes.length; i++) {
    const node = jsonNodes[i];
    if (node) {
      jsonNodeMap.set(node._id, node);
    }
  }
  for (let i = 0; i < jsonSymbols.length; i++) {
    const symbol = jsonSymbols[i];
    if (symbol) {
      jsonNodeMap.set(symbol._id, symbol);
    }
  }

  const bindableCollectionId = dynCollection || null;
  const listCollectionId = null;
  const traversedNodes: {[nodeId: string]: true} = {};
  return _hasDateFilters(
    rootNodeId,
    jsonNodeMap,
    collections,
    bindableCollectionId,
    listCollectionId,
    traversedNodes
  );
};

type SymbolIdsMap = {
  [nodeId: string]: true;
};

/**
 * Collects component root ids from the node POJOs inside a field's default
 * value. Evaluated defaults use the same NodePojo shape that authoring writes:
 *
 * `{pluginType: '__SitePlugin', type: <componentRootId>}`
 *
 * A component's name is its root node's `_id`, so the type is already a key into
 * the symbols map. A default is a whole subtree — nested `children`, plus
 * overrides hanging off `data` — so this recurses through all of it. It takes
 * an unknown rather than an array because an Element field defaults to a single
 * POJO while a List field defaults to an array of them.
 */
const collectDefaultValueSymbolIds = (
  value: unknown,
  acc: Set<string>
): void => {
  if (Array.isArray(value)) {
    for (const item of value) {
      collectDefaultValueSymbolIds(item, acc);
    }
    return;
  }

  if (!value || typeof value !== 'object') return;

  if (
    isNodePojo(value) &&
    value.pluginType === '__SitePlugin' &&
    typeof value.type === 'string'
  ) {
    acc.add(value.type);
  }

  for (const child of Object.values(value)) {
    collectDefaultValueSymbolIds(child, acc);
  }
};

/**
 * Collects component root ids from the element expressions inside a component's
 * `defaultData`. The value may arrive as either a JSON-stringified Expression
 * (new write path) or the legacy in-object Expression.
 */
const collectDefaultDataSymbolIds = (value: Json, acc: Set<string>): void => {
  const parsedDefaultData = parseDefaultData(value);
  if (!parsedDefaultData) return;

  for (const element of Object.values(
    Expressions.allElements(parsedDefaultData)
  )) {
    if (getPluginName(element.type) !== '__SitePlugin') continue;
    acc.add(getName(element.type));
  }
};

/**
 * Component root ids referenced by a definition's field defaults.
 *
 * Defaults (slot defaults especially) live on the definition rather than in any
 * render, so a component used only as another component's default has no
 * instance node to find. The renderer still fills every un-overridden field
 * from its default, so the default renders at publish and SSR 500s if its
 * definition was pruned from the page.
 *
 * Both at-rest shapes are read. `defaultData` is where authoring writes today;
 * unmigrated definitions — and anything AI Site Gen writes before a component
 * is enrolled — still carry POJOs on the field's `system.defaultValue`, and
 * `getSlotDefaultElements` still reads those, so pruning has to see them too.
 *
 * Ids that match no symbol are dropped by the caller's map lookup.
 */
export const getSlotDefaultSymbolIds = weakMemo(
  (symbol: JSONNode): string[] => {
    const ids = new Set<string>();

    const defaultData = symbol.data?.sym?.defaultData;
    if (defaultData !== undefined) {
      collectDefaultDataSymbolIds(defaultData, ids);
    }

    const rawDataType = symbol.dataType;
    const dataType =
      typeof rawDataType === 'string'
        ? (parse(rawDataType) as undefined | Type)
        : rawDataType;
    const fields = dataType ? Types.getRecordFields(dataType) : null;
    if (fields) {
      for (const fieldType of Object.values(fields)) {
        if (!fieldType) continue;
        collectDefaultValueSymbolIds(getSystem(fieldType)?.defaultValue, ids);
      }
    }

    return ids.size === 0 ? emptyArray : [...ids];
  }
);

const getSymbolsIdsMap = (
  jsonSymbolsMap: {
    [nodeId: string]: JSONNode;
  },
  symbolId: string,
  traversedSymbolIds: SymbolIdsMap = {}
): SymbolIdsMap => {
  const symbol = jsonSymbolsMap[symbolId];

  if (!symbol || traversedSymbolIds[symbolId]) {
    return traversedSymbolIds;
  }

  traversedSymbolIds[symbolId] = true;

  // Definitions referenced only by this one's field defaults have no instance
  // node, so the render walk below can't reach them.
  for (const defaultSymbolId of getSlotDefaultSymbolIds(symbol)) {
    getSymbolsIdsMap(jsonSymbolsMap, defaultSymbolId, traversedSymbolIds);
  }

  const symbolRootId = getSymbolRootIdFromInstance(symbol);

  if (symbolRootId) {
    forEachComponentPropertyChild(symbol, (childId) => {
      getSymbolsIdsMap(jsonSymbolsMap, childId, traversedSymbolIds);
    });

    getSymbolsIdsMap(jsonSymbolsMap, symbolRootId, traversedSymbolIds);
  } else if (Array.isArray(symbol.children)) {
    for (const childId of symbol.children) {
      if (!traversedSymbolIds[childId]) {
        getSymbolsIdsMap(jsonSymbolsMap, childId, traversedSymbolIds);
      }
    }
  }

  return traversedSymbolIds;
};

const getDefaultElementDataAndBind = getElementDataAndBindForJSONNode(
  QualifiedMap.empty()
);

const getSlotDefaultComponentInstances = weakMemo(
  (symbol: JSONNode): Map<string, ExpressionElement[]> => {
    const instances = new Map<string, ExpressionElement[]>();
    const rawDefaultData = symbol.data?.sym?.defaultData;
    if (rawDefaultData === undefined) return instances;
    const defaultData = parseDefaultData(rawDefaultData);
    const body = defaultData && Expressions.getFunction(defaultData)?.body;
    const fields = body && Expressions.getRecord(body);
    if (!fields) return instances;
    for (const [fieldId, value] of Object.entries(fields)) {
      instances.set(
        fieldId,
        Object.values(Expressions.allElements(value)).filter(
          (element) => getPluginName(element.type) === '__SitePlugin'
        )
      );
    }
    return instances;
  }
);

const hasDateFiltersInSlotDefaults = (
  definition: JSONNode,
  overrides: JSONNode['data'],
  jsonNodeMap: Map<string, JSONNode>,
  collections: Parameters<typeof hasDateFilters>[3],
  traversedNodes: {[nodeId: string]: true},
  traversedDefaultInstances: WeakSet<ExpressionElement>
): boolean => {
  for (const [fieldId, instances] of getSlotDefaultComponentInstances(
    definition
  )) {
    if (Object.prototype.hasOwnProperty.call(overrides ?? {}, fieldId))
      continue;
    for (const instance of instances) {
      if (traversedDefaultInstances.has(instance)) continue;
      traversedDefaultInstances.add(instance);

      const componentId = getName(instance.type);
      const componentNode = jsonNodeMap.get(componentId);
      if (!componentNode) continue;
      const {data} = getDefaultElementDataAndBind(instance);
      const instanceOverrides = data?.sym?.overrides ?? data;
      if (
        hasRelativeDateComparisonPredicates(instance.data) ||
        hasComponentFilterPropertiesWithDate(
          componentNode,
          instanceOverrides,
          collections
        ) ||
        hasDateFiltersInSlotDefaults(
          componentNode,
          instanceOverrides,
          jsonNodeMap,
          collections,
          traversedNodes,
          traversedDefaultInstances
        ) ||
        _hasDateFilters(
          componentId,
          jsonNodeMap,
          collections,
          null,
          null,
          traversedNodes,
          traversedDefaultInstances
        )
      )
        return true;
    }
  }
  return false;
};

const _hasDateFilters = (
  nodeId: string,
  jsonNodeMap: Map<string, JSONNode>,
  collections: {
    [collectionId: string]: {
      fields: {
        slug: string;
        type: string;
        validations?: {collectionId?: string};
      }[];
    };
  },
  bindableCollectionId: string | null,
  listCollectionId: string | null,
  traversedNodes: {[nodeId: string]: true},
  traversedDefaultInstances = new WeakSet<ExpressionElement>()
): boolean => {
  // Checking for traversed nodes so that circular
  // nodes would not cause infinite loop.
  if (traversedNodes[nodeId]) {
    return false;
  }
  traversedNodes[nodeId] = true;

  const jsonNode = jsonNodeMap.get(nodeId);

  if (!jsonNode) {
    return false;
  }

  const isDynamoWrapper = DomTypeUtils.isDynamoWrapper(jsonNode.data);
  if (isDynamoWrapper) {
    listCollectionId = jsonNode.data?.dyn?.collection?.id || null;
  } else if (DomTypeUtils.isDynamoItem(jsonNode.data)) {
    bindableCollectionId = listCollectionId;
  }

  const hasDateFilter = isDynamoWrapper
    ? isDynamicListWithDateFilter(jsonNode, collections, listCollectionId)
    : hasConditionalVisibilityWithDate(
        jsonNode,
        collections,
        bindableCollectionId
      );

  if (hasDateFilter) {
    return true;
  }

  const symbolRootId = getSymbolRootIdFromInstance(jsonNode);

  if (symbolRootId) {
    let hasDateFilterInProperty = false;
    forEachComponentPropertyChild(jsonNode, (childId) => {
      if (!hasDateFilterInProperty) {
        hasDateFilterInProperty = _hasDateFilters(
          childId,
          jsonNodeMap,
          collections,
          bindableCollectionId,
          listCollectionId,
          traversedNodes,
          traversedDefaultInstances
        );
      }
    });
    if (hasDateFilterInProperty) return true;

    const componentNode = jsonNodeMap.get(symbolRootId);
    if (!componentNode) return false;
    if (
      hasComponentFilterPropertiesWithDate(
        componentNode,
        jsonNode.data?.sym?.overrides,
        collections
      ) ||
      hasDateFiltersInSlotDefaults(
        componentNode,
        jsonNode.data?.sym?.overrides,
        jsonNodeMap,
        collections,
        traversedNodes,
        traversedDefaultInstances
      )
    )
      return true;

    return _hasDateFilters(
      symbolRootId,
      jsonNodeMap,
      collections,
      bindableCollectionId,
      listCollectionId,
      traversedNodes,
      traversedDefaultInstances
    );
  } else if (jsonNode.children) {
    return jsonNode.children.some((childNodeId) =>
      _hasDateFilters(
        childNodeId,
        jsonNodeMap,
        collections,
        bindableCollectionId,
        listCollectionId,
        traversedNodes,
        traversedDefaultInstances
      )
    );
  }

  return false;
};

type FiltersFromComponentDataType = Array<{
  propertyId: string;
  type: Type;
  collectionId: string;
}>;
/**
 * Find collection list filter in the dataType of a component root node.
 *
 * Given a JSON node representing the component root, find any assumed filter properties
 * in the component dataType and return it.
 *
 * Note! This method assumes the caller has already validated that the node
 * represents a component root.
 *
 * @param rootNode - a JSON node representing a component root
 */
export const getFiltersFromComponentDataType = weakMemo(
  (rootNode: JSONNode): FiltersFromComponentDataType => {
    const rawDataType = rootNode.dataType;
    if (rawDataType) {
      const dataType =
        typeof rawDataType === 'string'
          ? (parse(rawDataType) as undefined | Type)
          : rawDataType;

      const fields = dataType ? Types.getRecordFields(dataType) : null;
      if (fields) {
        return Object.entries(fields).reduce<FiltersFromComponentDataType>(
          (res, [propertyId, type]) => {
            const name = Types.getTypeConstructor(type);
            if (name) {
              const parsed = parseCollectionListPropTypeName(name);
              if (parsed && parsed.propType === 'Filter') {
                res.push({propertyId, type, collectionId: parsed.collectionId});
              }
            }
            return res;
          },
          []
        );
      }
    }
    return [];
  }
);

const hasComponentFilterPropertiesWithDate = (
  componentNode: JSONNode,
  overrides: JSONNode['data'],
  collections: Parameters<typeof hasDateFilters>[3]
): boolean => {
  const filterProps = getFiltersFromComponentDataType(componentNode);
  for (const {propertyId, collectionId, type} of filterProps) {
    const override = overrides ? overrides[propertyId] : undefined;
    let fields: unknown[] | undefined;
    if (override) {
      if (Array.isArray(override.fields)) {
        fields = override.fields;
      }
    } else {
      const defaultValue = Types.getDefaultValue(type, null);
      if (defaultValue && Array.isArray(defaultValue.fields)) {
        fields = defaultValue.fields;
      }
    }

    // check if there is a relative date filter in the filter fields
    if (
      Array.isArray(fields) &&
      collections[collectionId] &&
      hasRelativeDateConditionWhenConditionsAreAnArray(
        fields,
        collections[collectionId]
      )
    ) {
      return true;
    }
  }

  return false;
};

const isDynamicListWithDateFilter = (
  node: JSONNode,
  collections: {
    [collectionId: string]: {
      fields: {
        slug: string;
        type: string;
        validations?: {collectionId?: string};
      }[];
    };
  },
  collectionId: string | null
) => {
  const filters = collectionId && node.data?.dyn?.query?.filter?.fields;
  return hasRelativeDateCondition(filters, collections, collectionId);
};

const hasConditionalVisibilityWithDate = (
  node: JSONNode,
  collections: {
    [collectionId: string]: {
      fields: {
        slug: string;
        type: string;
        validations?: {collectionId?: string};
      }[];
    };
  },
  collectionId: string | null
) => {
  const conditions =
    node.data &&
    node.data.dyn &&
    node.data.dyn.condition &&
    node.data.dyn.condition.class &&
    node.data.dyn.condition.class.false &&
    node.data.dyn.condition.class.false[CONDITION_INVISIBLE_CLASS] &&
    node.data.dyn.condition.class.false[CONDITION_INVISIBLE_CLASS].fields;

  if (hasRelativeDateCondition(conditions, collections, collectionId)) {
    return true;
  }

  if (node.bind && typeof node.bind === 'object') {
    return hasRelativeDateComparisonPredicates(node.bind);
  }

  return false;
};

// Note this on purpose excludes dynamoDateIsSet and dynamoDateTimeIsSet as they don't support relative dates
const dateComparisonPredicatesWithDateCondition = [
  // Date predicates
  'dynamoDateIs',
  'dynamoDateIsNot',
  'dynamoDateIsBefore',
  'dynamoDateIsOnOrBefore',
  'dynamoDateIsAfter',
  'dynamoDateIsOnOrAfter',
  // DateTime predicates
  'dynamoDateTimeIs',
  'dynamoDateTimeIsNot',
  'dynamoDateTimeIsBefore',
  'dynamoDateTimeIsOnOrBefore',
  'dynamoDateTimeIsAfter',
  'dynamoDateTimeIsOnOrAfter',
].reduce((acc, name) => {
  return acc.set(qualify('DynamoGateway', name), true);
}, QualifiedMap.empty<string, boolean>());

const isDateComparisonPredicateRef = Variable.match({
  qualified: (name) => dateComparisonPredicatesWithDateCondition.get(name),
  unqualified: constantFalse,
});

const isDateComparisonPredicateWithRelativeConditionValue =
  matchExpression<boolean>({
    Call: ({fun, arg}) => {
      const funVar = Expressions.getVariable(fun);
      if (funVar && isDateComparisonPredicateRef(funVar)) {
        const union = Expressions.getUnion(arg);
        return Boolean(
          union && (union.tag === 'Relative' || union.tag === 'Today')
        );
      }
      return false;
    },
    _: constantFalse,
  });

const hasRelativeDateComparisonPredicates = Expressions.some(
  isDateComparisonPredicateWithRelativeConditionValue
);

const hasRelativeDateCondition = (
  conditions: unknown,
  collections: {
    [collectionId: string]: {
      fields: {
        slug: string;
        type: string;
        validations?: {collectionId?: string};
      }[];
    };
  },
  collectionId: string | null
) => {
  if (
    !conditions ||
    !collections ||
    !collectionId ||
    !collections[collectionId]
  ) {
    return false;
  }
  if (Array.isArray(conditions)) {
    return hasRelativeDateConditionWhenConditionsAreAnArray(
      conditions,
      collections[collectionId]
    );
  }
  const fields = collections[collectionId].fields;
  return Object.entries(conditions).some(
    ([fieldSlug, dateFilter]: [string, any]) => {
      if (!dateFilter || (!dateFilter.gte && !dateFilter.lte)) {
        return false;
      }

      if (isFieldOfItemRef(fieldSlug)) {
        const referrerField = fields.find(
          ({slug}) => slug === getItemRefSlug(fieldSlug)
        );
        const referencedCollectionId =
          referrerField &&
          referrerField.validations &&
          referrerField.validations.collectionId;
        const referencedCollection = referencedCollectionId
          ? collections[referencedCollectionId]
          : null;
        const referencedField =
          referencedCollection &&
          referencedCollection.fields &&
          referencedCollection.fields.find(
            ({slug}) => slug === getValueFieldSlug(fieldSlug)
          );
        return referencedField && referencedField.type === 'Date';
      }

      const field = fields.find(({slug}) => slug === fieldSlug);
      return field && field.type === 'Date';
    }
  );
};

const hasRelativeDateConditionWhenConditionsAreAnArray = (
  conditions: unknown[],
  collection: {
    fields: {
      slug: string;
      type: string;
      validations?: {collectionId?: string};
    }[];
  }
) => {
  const fields = collection && collection.fields;

  if (!fields) {
    return false;
  }

  // we only care about relative (before or after) date filters
  return conditions.some((cond) => {
    // make sure conditions have the right shape (mostly for TS type safety)
    const isValidCondition =
      cond &&
      typeof cond === 'object' &&
      'fieldPath' in cond &&
      'operatorName' in cond;

    return (
      isValidCondition &&
      // so we can rule out any filter where operator is not gte or lte, and
      (cond.operatorName === 'gte' || cond.operatorName === 'lte') &&
      // then do the more expensive check to see if the field is a date field
      fields.some(
        (field) => field.slug === cond.fieldPath && field.type === 'Date'
      )
    );
  });
};

const richTextStringToJsonNodes = (value: undefined | string) =>
  rteHtmlToJsonNodes(value, {
    removeRootLevelSpaces: true,
    allowDynLink: true,
    addType: true,
    // @ts-expect-error - TS7006 - Parameter 'err' implicitly has an 'any' type.
    onError: (err) => {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug('HtmlToAbstractJsonParseError', 'HTML -> JSON parsing error', {
        value,
        error: err.name,
        message: err.message,
      });
    },
    supportHtmlEmbeds: true,
  });

/**
 * Convert a RichText value to an array of JSONNode
 *
 * If you also need to identify the root nodes use
 * `getRichTextValueData` instead.
 */
export const getRichTextValueJsonNodes = (
  value?: RichTextValue
): JSONNode[] => {
  if (!value || typeof value === 'string') {
    return richTextStringToJsonNodes(value).jsonContentNodes;
  }

  return Array.isArray(value)
    ? value
    : // have to cast toJS() here because it returns Array<mixed>
      // the reason it's using immutable.js is the `.tline in RequestUtils here:
      // https://github.com/webflow/webflow/blob/df3dcb144515dbc924c17adca5f87371f0a7549c/public/js/designer-flux/utils/RequestUtils.js#L49
      (value.toJS() as Array<JSONNode>);
};

/**
 * Given a RichText value, it returns the value converted to an array
 * of JSONNode and an array of IDs of the root JSONNodes.
 *
 * Use `getRichTextValueJsonNodes` instead if you don’t need the root IDs.
 */
export const getRichTextValueData = (
  plugins: Plugins.PluginsType,
  dataTypes: Plugins.DataTypes,
  value?: RichTextValue
): {
  jsonContentNodes: JSONNode[];
  rootChildIds: string[];
} => {
  if (!value || typeof value === 'string') {
    return richTextStringToJsonNodes(value);
  }

  const jsonContentNodes = getRichTextValueJsonNodes(value);

  return filterDivergentRichTextNodes(plugins, dataTypes, jsonContentNodes);
};
